import { useState, useContext, useRef } from "react"
import { useForm } from "react-hook-form"
import useTranslations from "hooks/useTranslations"
import { LocaleContext } from "contexts"
import { subscribeToNewsletter } from "utils/forms"
import { Input, SubmitButton, PrivacyPolicyLink } from "components/atoms/form"
import { Modal } from "components/atoms/common/Modal"
import { siteCountry } from "utils/env"
import Altcha from "components/atoms/Altcha"
import countries from "../../config/i18n"

const locales = countries[siteCountry] || countries.fi

type SubscribeFormProps = {
  title?: string
  segment?: "schools" | "pro" | ""
}

const SubscribeForm = ({ title = "", segment = "" }) => {
  const {
    subscribeHeader,
    subscribeHeaderSchool,
    subscribeButton,
    subscribePrivacyPolicy1,
    formErrorGeneral,
    formFieldEmail,
    formErrorUserExist,
    formErrorContactUs,
    newsletterFormSuccessTitle,
    newsletterFormSuccessBody,
    webinarFormSuccessButton,
  } = useTranslations()

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = useForm()

  const { locale } = useContext(LocaleContext)
  const [isSubmitError, setSubmitError] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [errMessage, setErrMessage] = useState(formErrorGeneral)
  const altchaRef = useRef<{ value: string | null }>(null)

  const onSubmit = async (data) => {
    try {
      let res
      const altcha = altchaRef.current?.value
      if (process.env.NODE_ENV === "development") {
        res = { status: 200 }
      } else {
        res = await subscribeToNewsletter(data, locale, segment, altcha)
      }
      if (res.status === 200) {
        setShowModal(true)
        reset()
        setSubmitError(false)
      }
    } catch ({ response }) {
      if (response?.data?.title === "Invalid Resource") {
        setErrMessage(response.data.detail)
      } else if (response?.data?.title === "Member Exists") {
        setErrMessage(formErrorUserExist)
      }
      setSubmitError(true)
    }
  }

  return (
    <div
      className="relative bg-white py-16 lg:py-24"
      id="newsletter-subscription"
    >
      <div className="relative mx-auto max-w-7xl px-4 lg:px-8">
        <div className=" rounded-2xl bg-kkm-green lg:flex lg:items-center">
          <div className="px-6 py-8 md:pl-12 lg:flex-1 lg:pr-0">
            <h2 className="mb-0 text-xl font-extrabold -tracking-wide text-slate-900 sm:text-3xl sm:leading-8">
              {title ||
                (segment === "schools"
                  ? subscribeHeaderSchool
                  : subscribeHeader)}
            </h2>
          </div>

          <div className="relative flex-1 lg:ml-8 lg:max-w-lg">
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="mb-0 space-y-4 bg-white/30 p-6 md:px-12"
            >
              <Input
                label={formFieldEmail}
                name="email"
                control={control}
                required
                type="email"
                errors={errors}
              />
              <div className="text-sm font-bold text-zinc-700 ">
                <Altcha
                  ref={altchaRef}
                  style={{
                    "--altcha-border-radius": "0.375rem",
                    "--altcha-color-border": "rgb(212, 212, 216)",
                    "--altcha-color-active": "teal",
                    "--altcha-color-border-focus": "#2563eb",
                    "--altcha-color-base": "rgba(255, 255, 255, 0.5)",
                  }}
                />
              </div>
              <PrivacyPolicyLink text={subscribePrivacyPolicy1} />
              <SubmitButton
                label={subscribeButton}
                isSubmitting={isSubmitting}
                isSubmitSuccessful={isSubmitSuccessful}
                isSubmitError={isSubmitError}
                errorMessage={`${errMessage} ${formErrorContactUs} ${locales[locale].contactEmail}`}
              />
            </form>
          </div>
        </div>
      </div>
      <Modal
        isOpen={showModal}
        setIsOpen={setShowModal}
        title={newsletterFormSuccessTitle}
        bodyText={newsletterFormSuccessBody}
        buttonText={webinarFormSuccessButton}
      />
    </div>
  )
}

export default SubscribeForm
